import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Bell,
  CalendarDays,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Sun,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { useSession } from '@/app/session-context';
import badge from '@/assets/asya-badge.png';
import badgeDark from '@/assets/asya-badge-dark.png';
import lockup from '@/assets/asya-lockup.png';
import lockupDark from '@/assets/asya-lockup-dark.png';
import { useTheme } from '@/app/theme';
import { NAV } from '@/app/nav';
import { NavMenuList, SideNav } from './SideNav';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { SearchTrigger } from '@/features/search/SearchTrigger';
import { ReportButton } from '@/features/support/ReportButton';
import { useUnreadCount } from '@/features/notifications/api';
import { WhatsNewDialog } from '@/features/release-notes/WhatsNewDialog';
import { YeniSurumBandi } from '@/features/release-notes/YeniSurumBandi';
import { ThemedImage } from '@/ui/ThemedImage';
import { Button, IconButton, Modal, cx } from '@/ui/primitives';

interface Tab {
  key: string;
  to: string;
  label: string;
  Icon: LucideIcon;
  badge?: number;
  end?: boolean;
}

/** The three nav items that earn a permanent place on a phone, by nav key. */
const TAB_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  approvals: ClipboardCheck,
  'my-leave': CalendarDays,
};
const TAB_ORDER = ['dashboard', 'approvals', 'my-leave'];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout, can } = useSession();
  const { theme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const unread = useUnreadCount();

  const visible = NAV.filter((item) => item.requires.every((p) => can(p)));

  // A menu that survives the navigation it just caused leaves the person
  // looking at the menu instead of the page they asked for.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /*
   * The bottom bar, on a phone.
   *
   * Eighteen menu entries behind a hamburger means the two screens this app
   * exists for — "what is waiting on me" and "where is my leave" — cost two
   * taps and a read. Here they cost one tap and are on screen the whole time,
   * which is the difference between approving from a phone and meaning to.
   *
   * Built from the permitted nav, so a user without the approval permission
   * gets a four-tab bar rather than a tab that 403s.
   */
  const tabs: Tab[] = [
    ...TAB_ORDER.flatMap((key) => {
      const item = visible.find((i) => i.key === key);
      if (!item?.ready) return [];
      return [
        {
          key: item.key,
          to: item.to,
          label: item.label,
          Icon: TAB_ICONS[key] ?? LayoutDashboard,
          end: item.to === '/',
        },
      ];
    }),
    {
      key: 'notifications',
      to: '/bildirimler',
      label: 'Bildirim',
      Icon: Bell,
      badge: unread.data?.count ?? 0,
    },
  ];

  return (
    <div className="bg-bg flex min-h-dvh flex-col">
      <header className="border-border bg-surface pt-safe px-safe sticky top-0 z-20 border-b">
        {/* 56px on a phone, 76px from md up. The taller row exists for one
            reason: the lockup is 1.86:1, so in 56px its "ASYA ASC HAVACILIK"
            band renders about six pixels tall, which is a smudge. At 76px the
            mark can be 56px and the band lands near nine, which reads. The
            phone keeps 56 because the mark is hidden there anyway.
            `SideNav` sticks under this row — its `md:top-*` and its height
            calc are the same number, and they move together or the column
            slides under the header. */}
        <div className="flex h-14 items-center gap-1 px-2 md:h-[4.75rem] md:gap-3 md:px-4">
          {/* The WORDMARK strip, not the full lockup.
              The lockup is 1.87:1, so in a 56px row it renders 67px wide and
              its "ASYA ASC HAVACILIK" line lands about three pixels tall — a
              smudge rather than a logo. Legibility there would need ~56px of
              height, which is the entire row. The band alone is 4.64:1: at
              28px it runs 130px wide with ~13px letters, which reads.

              The band is the ELLIPSE, and nothing above it. The first crop cut
              the artwork on a straight line through the "A", so the strip
              carried an amputated letter and a slice of wing floating over the
              oval. Small, it stopped looking like a fragment and started
              looking like a fault in the alignment.

              WHERE IT LIVES — read this before moving it a seventh time.
              Top-left, first in the row, because the product owner asked for it
              there on 2026-08-15 while looking at the running app. It has been
              on the right (#148, #163) and briefly at the far right after
              sign-out earlier today; both are now superseded. The old argument
              against the right-hand end — that past the controls a mark reads
              as one more control — is exactly why the corner it belongs in is
              this one: nothing precedes it, so nothing can mistake it for a
              control. Do not move it back without asking the owner.

              `shrink-0` and `max-w-[130px]` with `object-contain`: the row is a
              flex line, and without them a long user name or a wide search box
              compresses this cell and the artwork spills past its box instead
              of scaling. Height is the constraint that matters (h-7 in a 56px
              row); the width cap only stops a future, wider file from pushing
              the search box off-centre.

              Desktop only, and that is a budget rather than a preference: the
              shell's own note below puts five controls in a 360px row already,
              and the 130px this needs is a third of that row. So a phone
              carries the name and no mark. Decorative either way — the name
              beside it is the link home, and this is not clickable. */}
          {/* The phone's mark, and the reason it is the SQUARE badge rather than
              the strip above: the cell above is desktop-only on a width budget,
              and 130px of a 375px row is a third of it. The badge is 1:1, so at
              h-7 it costs 28px — the same money the row already spends on one
              icon. The owner asked for the mark on a phone on 2026-08-17, having
              found the corner empty there.

              It cannot bring back the "ASYA …" truncation that removing a 32px
              badge fixed. At 360px the row spends 8+8 of padding, 44×3 for
              search, bell and report — `IconButton` is `size-11` here and only
              `md:size-9` above — and 4×5 of gap, which is 168; this adds 28 and
              the name needs 96, leaving 68px of spacer. The name runs out of
              room below 292px, and no phone is that narrow.

              The mark appears ONCE at every width: this is `md:hidden` and the
              lockup below is `hidden md:flex`. That is the point — one logo held
              at both ends of a row reads as a mark torn in two. */}
          <ThemedImage
            light={badge}
            dark={badgeDark}
            alt=""
            className="h-7 w-7 shrink-0 object-contain md:hidden"
          />

          <div className="border-border hidden shrink-0 items-center border-r pr-3 md:flex">
            <ThemedImage
              light={lockup}
              dark={lockupDark}
              alt=""
              className="h-14 w-auto max-w-[110px] object-contain"
            />
          </div>

          {/* The name of the app, and the way home. The badge that used to sit
              here was the LEFT HALF of the same lockup whose right half is the
              strip above; one logo held at both ends of a row read as a mark
              torn in two, so the mark now appears once and this stays text. It
              also stops truncating to "ASYA …" at 360px, the 32px badge having
              been what pushed it over. */}
          <NavLink to="/" className="flex min-w-0 items-center px-2">
            <span className="text-fg truncate text-sm font-semibold">ASYA GRUP</span>
          </NavLink>

          {/* Takes over the bare `flex-1` spacer that used to be here: a search
              box on desktop, a spacer plus one icon on a phone. Everything to
              the right of it keeps its position and its width. */}
          <SearchTrigger />

          <NotificationBell />
          {/* Next to the bell on every width, phone included. The screen a
              problem happens on is the screen it should be reported from, and
              on a phone that is the only screen there is. */}
          <ReportButton />

          {/* Theme, profile and sign-out live in the phone menu instead: at
              360px, five header controls leave no room for the name they are
              meant to belong to. */}
          <IconButton
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
            className="hidden md:inline-flex"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </IconButton>

          <NavLink
            to="/profil"
            className="hover:bg-surface-muted hidden items-center gap-2 rounded-lg px-2 py-1.5 transition md:flex"
            title="Profil ve güvenlik"
          >
            <div className="hidden min-w-0 text-right lg:block">
              <p className="text-fg truncate text-sm leading-tight font-medium">{user?.fullName}</p>
              <p className="text-fg-muted truncate text-xs leading-tight">{user?.email}</p>
            </div>
            <UserCog size={18} className="text-fg-muted" />
          </NavLink>

          <IconButton
            onClick={() => void logout()}
            className="hover:text-danger hidden md:inline-flex"
            aria-label="Çıkış yap"
            title="Çıkış yap"
          >
            <LogOut size={18} />
          </IconButton>
        </div>
      </header>

      {/* Açık sekmeye «yeni sürüm yayınlandı» — DST-00078. Başlığın altında,
          çünkü hangi ekranda olunursa olunsun görünmeli. */}
      <YeniSurumBandi />

      <div className="flex flex-1">
        {/* The desktop column owns its own markup, icons, active state and
            collapse preference — see SideNav. The shell only says where it
            goes. */}
        <SideNav />

        {/* pb-nav clears the bottom bar, so the last row of a list is reachable
            rather than permanently tucked behind it. */}
        <main
          // `px-safe-4`, not `px-safe` + `p-4`: the second pair is a
          // replacement rather than an addition, so on a phone in portrait —
          // where the inset is 0px — every screen lost its horizontal padding
          // and ran edge-to-edge. `py-*` stays separate because only the
          // horizontal axis collides.
          className="pb-nav min-w-0 flex-1 px-safe-4 py-4 md:px-safe-6 md:py-6 md:pb-6"
        >
          {children}
        </main>
      </div>

      <nav
        aria-label="Ana gezinme"
        className="border-border bg-surface pb-safe-2 px-safe fixed inset-x-0 bottom-0 z-30 border-t md:hidden"
      >
        <div className="mx-auto flex max-w-lg items-stretch">
          {tabs.map(({ key, to, label, Icon, badge, end }) => (
            <NavLink
              key={key}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  'relative flex flex-1 touch-manipulation flex-col items-center justify-center gap-0.5 pt-2 pb-1 text-[11px] transition',
                  isActive ? 'text-primary font-medium' : 'text-fg-muted active:text-fg',
                )
              }
            >
              <span className="relative">
                <Icon size={21} />
                {badge !== undefined && badge > 0 && (
                  <span className="bg-danger absolute -top-1 -right-2 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold text-white">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              {label}
            </NavLink>
          ))}

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Menüyü aç"
            aria-expanded={menuOpen}
            className="text-fg-muted active:text-fg flex flex-1 touch-manipulation flex-col items-center justify-center gap-0.5 pt-2 pb-1 text-[11px] transition"
          >
            <Menu size={21} />
            Menü
          </button>
        </div>
      </nav>

      <Modal open={menuOpen} title="Menü" onClose={() => setMenuOpen(false)}>
        {/* Same component as the desktop column, always labelled: one menu,
            rendered twice, rather than two menus kept in step by hand. */}
        <NavMenuList onNavigate={() => setMenuOpen(false)} />

        <div className="border-border mt-4 border-t pt-4">
          <NavLink
            to="/profil"
            onClick={() => setMenuOpen(false)}
            className="hover:bg-surface-muted active:bg-surface-muted flex min-w-0 items-center gap-3 rounded-lg p-2 transition"
          >
            <span className="bg-surface-muted text-fg-muted flex size-9 shrink-0 items-center justify-center rounded-full">
              <UserCog size={17} />
            </span>
            <span className="min-w-0">
              <span className="text-fg block truncate text-sm font-medium">{user?.fullName}</span>
              <span className="text-fg-muted block truncate text-xs">{user?.email}</span>
            </span>
          </NavLink>

          <div className="mt-3 flex gap-2">
            <Button block onClick={toggle}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              {theme === 'dark' ? 'Açık tema' : 'Koyu tema'}
            </Button>
            <Button block onClick={() => void logout()} className="hover:text-danger">
              <LogOut size={16} /> Çıkış
            </Button>
          </div>
        </div>
      </Modal>

      {/* Mounted in the shell, not on a route: whoever logs in first after a
          deploy is the person who needs to be told what changed, and there is
          no telling which screen they land on. It renders nothing at all until
          the notes are loaded and the newest version is one this browser has
          not been shown. */}
      <WhatsNewDialog />
    </div>
  );
}
